/**
 * Where the bearer token lives.
 *
 * The backend issues opaque bearer tokens (Identity's BearerToken scheme), not cookies, so the SPA has
 * to hold them itself. localStorage keeps a reload signed in; the token expires on the server side
 * regardless, and the account behind it can be locked out there.
 */

const STORAGE_KEY = 'covdbg.session';

// Refresh this far ahead of the stated expiry, so a long request cannot start on a token that expires
// while it is in flight.
const EXPIRY_SKEW_MS = 60_000;

let current = read();

const listeners = new Set();

function read()
{
   try
   {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
   }
   catch
   {
      return null;
   }
}

function write(session)
{
   current = session;

   if (session)
   {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
   }
   else
   {
      localStorage.removeItem(STORAGE_KEY);
   }

   for (const listener of listeners)
   {
      listener(current);
   }
}

export function getSession()
{
   return current;
}

export function isSignedIn()
{
   return Boolean(current?.accessToken);
}

export function getUserName()
{
   return current?.userName ?? null;
}

/** The roles the signed-in operator holds, as the server reported them at sign-in. Empty when unknown. */
export function getRoles()
{
   return Array.isArray(current?.roles) ? current.roles : [];
}

/** Whether the operator holds a role. A page uses it to leave out what the server would refuse. */
export function hasRole(role)
{
   return getRoles().some(held => String(held).toLowerCase() === String(role).toLowerCase());
}

/** Records the roles once they are known, without touching the tokens. */
export function setRoles(roles)
{
   if (current)
   {
      write({ ...current, roles: Array.isArray(roles) ? roles : [] });
   }
}

/** True once the access token is close enough to expiry that it should be refreshed before use. */
export function isExpiring()
{
   return !current?.expiresAt || current.expiresAt - EXPIRY_SKEW_MS <= Date.now();
}

/** Stores the token response from /api/v1/auth/login or /api/v1/auth/refresh. */
export function setSession(tokens, userName = current?.userName ?? null)
{
   write({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: Date.now() + (tokens.expiresIn ?? 3600) * 1000,
      userName,

      // Kept across a refresh: the roles were reported at sign-in and a new access token changes none.
      roles: current?.roles ?? []
   });
}

export function clearSession()
{
   write(null);
}

/** Notifies on sign-in and sign-out, so the shell can redraw without the views telling it to. */
export function onSessionChange(listener)
{
   listeners.add(listener);
   return () => listeners.delete(listener);
}
